import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MapPin, Sparkles, Heart, Shirt, ShieldCheck, Recycle, Zap, Crown, ShoppingBag, 
  ArrowRight, X, CheckCircle2, RefreshCcw, Quote, ChevronRight, Menu, Search,
  Globe, Package, Headphones, Award, Twitter, Instagram, Facebook, Youtube, 
  Component, Maximize, Tractor, Watch, Footprints, Layers, wishlistItems, MessageCircle, Send, Loader2, ChevronDown
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectCreative } from "swiper/modules";
import axios from "axios";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-creative";

import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import Auth from "./Auth";
import SEO from '../components/SEO';



// --- ADVANCED HUD ALERT (Responsive Enhanced) ---
const CustomAlert = ({ message, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div className={`fixed top-0 md:top-4 left-0 md:left-1/2 right-0 md:right-auto z-[9999] w-full md:w-auto md:min-w-[400px] transform transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
      <div className="bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 backdrop-blur-2xl text-white px-4 md:px-6 py-3 md:py-4 mx-4 md:mx-0 rounded-2xl md:rounded-[2rem] shadow-2xl border border-emerald-500/30 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="bg-emerald-500 p-1.5 md:p-2 rounded-full text-white shadow-lg shadow-emerald-500/30">
            <CheckCircle2 size={14} strokeWidth={3} />
          </div>
          <span className="font-black uppercase text-[11px] md:text-[12px] tracking-tight italic">{message}</span>
        </div>
        <button onClick={handleClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
          <X size={16} className="text-white/70"/>
        </button>
      </div>
    </div>
  );
};



// --- RESPONSIVE NAVIGATION MENU ---
const MobileMenu = ({ isOpen, onClose, isDarkMode, user }) => {
  const navigate = useNavigate();
  const menuRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const menuItems = [
    { label: "ARCHIVE", path: "/products", icon: <Shirt size={16} /> },
    { label: "NEW DROPS", path: "/new", icon: <Sparkles size={16} /> },
    { label: "ABOUT", path: "/about-us", icon: <Crown size={16} /> },
    { label: "VAULT", path: "/vault", icon: <ShieldCheck size={16} /> },
    { label: "SUPPORT", path: "/support", icon: <Headphones size={16} /> },
  ];

  return (
    <div ref={menuRef} className={`fixed top-0 left-0 right-0 z-[9000] transform transition-all duration-500 ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className={`${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'} min-h-screen pt-24 pb-12 px-6 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-md mx-auto space-y-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-black italic uppercase tracking-tighter">MENU</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5">
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => { navigate(item.path); onClose(); }}
                className={`w-full flex items-center justify-between p-4 rounded-2xl ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'} transition-all group`}
              >
                <div className="flex items-center gap-3">
                  <span className={`p-2 rounded-full ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                    {item.icon}
                  </span>
                  <span className="font-black uppercase text-[12px] tracking-widest">{item.label}</span>
                </div>
                <ChevronRight size={16} className="opacity-40 group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
          </div>

          <div className="pt-8 border-t border-white/10">
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-emerald-600/10">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <Crown size={20} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-60">WELCOME</p>
                <p className="font-black italic">{user?.username || 'USER'}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-6">
            {[Twitter, Instagram, Facebook, Youtube].map((Icon, i) => (
              <a key={i} href="#" className={`p-3 rounded-full ${isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'} transition-colors`}>
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- ENHANCED ADDRESS VAULT (Responsive) ---
const AddressModal = ({ user, isOpen, onClose, onUpdate, isDarkMode }) => {
  const [formData, setFormData] = useState({ address: "", city: "", pincode: "", state: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
    try {
      const token = user?.accessToken || user?.token;
      const res = await axios.put(
        `https://wethrift-backend.onrender.com/api/users/${user?._id}`,
        { address: fullAddress, phone: formData.phone },
        { headers: { token: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      onClose();
    } catch (err) { 
      alert("VAULT_SYNC_FAILED"); 
    } finally { 
      setLoading(false); 
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[8000] flex items-end md:items-center justify-center bg-black/95 backdrop-blur-xl px-0 md:px-4 animate-in fade-in duration-500">
      <div 
        ref={modalRef}
        className={`w-full max-w-2xl rounded-t-[3rem] md:rounded-[3.5rem] p-6 md:p-12 border-t md:border ${isDarkMode ? 'border-white/10' : 'border-black/10'} shadow-2xl animate-in slide-in-from-bottom-20 duration-500 ${isDarkMode ? "bg-[#0a0a0a] text-white" : "bg-white text-black"}`}
      >
        <div className="w-12 h-1.5 bg-zinc-800 rounded-full mx-auto mb-8 md:hidden" />
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter flex items-center gap-3">
            <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 bg-clip-text text-transparent">LOGISTICS</span>_VAULT
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-2 block">STREET_ADDRESS</label>
              <input 
                required 
                placeholder="ENTER_STREET_LINE"
                className={`w-full p-4 md:p-5 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all focus:ring-2 focus:ring-emerald-500/50 ${isDarkMode ? "bg-white/5 border-white/10 focus:bg-white/10" : "bg-black/5 border-black/10 focus:bg-black/10"}`}
                onChange={(e)=>setFormData({...formData, address: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-2 block">CITY</label>
              <input 
                required 
                placeholder="ENTER_CITY"
                className={`w-full p-4 md:p-5 rounded-2xl text-[10px] font-black uppercase outline-none border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-black/5 border-black/10"}`}
                onChange={(e)=>setFormData({...formData, city: e.target.value})} 
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-2 block">PIN_CODE</label>
              <input 
                required 
                placeholder="XXXXXX"
                className={`w-full p-4 md:p-5 rounded-2xl text-[10px] font-black uppercase outline-none border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-black/5 border-black/10"}`}
                onChange={(e)=>setFormData({...formData, pincode: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-2 block">CONTACT_ID</label>
              <input 
                required 
                placeholder="+91 79000 86873"
                className={`w-full p-4 md:p-5 rounded-2xl text-[10px] font-black uppercase outline-none border ${isDarkMode ? "bg-white/5 border-white/10" : "bg-black/5 border-black/10"}`}
                onChange={(e)=>setFormData({...formData, phone: e.target.value})} 
              />
            </div>
          </div>

          <button 
            type="submit" 
            className={`w-full py-4 md:py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-[11px] transition-all active:scale-95 mt-4 md:mt-8 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-black shadow-xl hover:shadow-2xl hover:shadow-emerald-500/20`}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCcw size={14} className="animate-spin" />
                INITIALIZING...
              </span>
            ) : "CONFIRM_IDENTITY"}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- RESPONSIVE PRODUCT CARD COMPONENT ---
const ProductCard = ({ product, isDarkMode, onQuickAdd, navigate }) => {
  const [isHovered, setIsHovered] = useState(false);
  const availableSize = product.sizeStock?.find(s => s.quantity > 0)?.size || "M";

  return (
    <div 
      className="group cursor-pointer"
      onClick={() => navigate(`/product/${product._id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative rounded-[1.5rem] md:rounded-[2rem] overflow-hidden transition-all duration-700 ${isDarkMode ? 'bg-[#0c0c0c]' : 'bg-white'} shadow-lg hover:shadow-2xl border ${isDarkMode ? 'border-white/5 hover:border-emerald-500/30' : 'border-black/5 hover:border-emerald-500/20'}`}>
        {/* Product Image with Hover Effect */}
        <div className="relative aspect-[3/4] overflow-hidden">
          <img 
            src={product.img || product.image} 
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
            crossOrigin="anonymous"
          />
          {/* Gradient Overlay on Hover */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
          
          {/* Quick Add Button - Different on Mobile vs Desktop */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onQuickAdd(product, availableSize);
            }}
            className={`absolute ${isHovered ? 'bottom-4' : 'bottom-4'} right-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-black p-3 md:p-4 rounded-full shadow-xl active:scale-90 transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/30`}
          >
            <ShoppingBag size={16} className="md:group-hover:scale-110 transition-transform" />
          </button>
        </div>

        {/* Product Info */}
        <div className="p-4 md:p-6">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-black uppercase italic tracking-tighter text-[12px] md:text-[14px] truncate flex-1">
              {product.name}
            </h3>
            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/10 text-emerald-600'}`}>
              {availableSize}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-lg md:text-xl font-black italic">₹{product.price}</p>
            <div className="flex items-center gap-1 opacity-60">
              <Heart size={12} />
              <span className="text-[9px] font-black uppercase">VAULTED</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const { user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { addToCart, wishlistItems, toggleWishlist } = useCart();
  const navigate = useNavigate();
  
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMsg, setAlertMsg] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);

// --- BULK MODAL LOGIC (Paste here) ---
const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
const [isSending, setIsSending] = useState(false);
const [isSuccess, setIsSuccess] = useState(false);
const bulkForm = useRef();

const WHATSAPP_NUMBER = "917900086873"; // ✅ No spaces, perfect for WhatsApp API

const openWhatsApp = () => {
  const msg = encodeURIComponent("Hello VEE THRIFT ARCHIVE Team! I'm looking for bulk inventory details. Please guide me.");
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
};

const handleBulkSubmit = async (e) => {
  e.preventDefault();
  setIsSending(true);
  const formData = {
    business_name: bulkForm.current.user_name.value, 
    email: bulkForm.current.user_email.value,        
    quantity: bulkForm.current.quantity.value,
    category: bulkForm.current.category.value,
    message: bulkForm.current.message.value,
  };
  try {
    const response = await fetch('https://wethrift-backend.onrender.com/api/bulk/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    const data = await response.json();
    if (response.ok && data.success) {
      setIsSuccess(true);
      bulkForm.current.reset();
      setTimeout(() => { setIsSuccess(false); setIsBulkModalOpen(false); }, 3500);
    } else { alert("Protocol Error: Backend check karo."); }
  } catch (error) { alert("Server Offline: Port 5000 check karo."); }
  finally { setIsSending(false); }
};

  // Scroll progress for header effect
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scroll = totalScroll / windowHeight;
      setScrollProgress(scroll);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

const categories = [
  { id: 1, name: "HOODIES", slug: "hoodies", icon: <Shirt size={22} /> }, // Hoodie ke liye Shirt (closet vibe)
  { id: 2, name: "JEANS", slug: "jeans", icon: <Component size={22} /> }, // Jeans ke liye structural icon
  { id: 3, name: "T-SHIRTS", slug: "t-shirts", icon: <Layers size={22} /> }, // T-shirts (stacking layers)
  { id: 4, name: "SHIRTS", slug: "shirts", icon: <Award size={22} /> }, // Premium shirts vibe
  { id: 5, name: "JACKETS", slug: "jackets", icon: <Zap size={22} /> }, // Jackets (energy/outerwear)
  { id: 6, name: "OVERSIZE_TEE", slug: "oversize_t-shirt", icon: <Maximize size={22} /> }, // Oversize (expand/maximize)
  { id: 7, name: "TRACK_PANTS", slug: "track_pants", icon: <Tractor size={22} /> }, // Track/Street utility
  { id: 8, name: "BAGGY_PANTS", slug: "baggy_pants", icon: <Watch size={22} /> }, // Baggy/Style statement
  { id: 9, name: "SHOES", slug: "shoes", icon: <Footprints size={22} /> }, // Shoes/Footwear
];


  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get("https://wethrift-backend.onrender.com/api/product");
        setProducts(data.reverse().slice(0, 8));
      } catch (err) { 
        console.error(err); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchProducts();
  }, []);

  // Address popup trigger
  useEffect(() => {
    if (user && !user.address) {
      const timer = setTimeout(() => setShowAddressPopup(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleQuickAdd = (product, size) => {
    addToCart(product, size);
    setAlertMsg("ASSET_LOCKED_IN_CART");
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user) return <Auth />;


  return (

<>
<SEO 
  title="Home | Rare Vintage & Streetwear" 
  description="Discover the ultimate collection of curated vintage assets. High-end thrifted streetwear delivered across India. Shop THE_ARCHIVE."
  url="/"
/>


    <div className={`min-h-screen overflow-x-hidden transition-colors duration-700 ${isDarkMode ? "bg-[#050505] text-white" : "bg-gradient-to-b from-[#fcfcfc] to-white text-black"}`}>
      
      {/* Custom Alert */}
      {alertMsg && <CustomAlert message={alertMsg} onClose={() => setAlertMsg("")} />}

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
        isDarkMode={isDarkMode}
        user={user}
      />

      {/* Address Modal */}
      <AddressModal
        user={user} 
        isOpen={showAddressPopup} 
        onClose={() => setShowAddressPopup(false)}
        onUpdate={(newData) => {
          const updated = { ...user, ...newData };
          localStorage.setItem("weThriftUser", JSON.stringify(updated));
          window.location.reload();
        }}
        isDarkMode={isDarkMode}
      />

      {/* Main Content */}
      <main className="pt-16 md:pt-20">
        {/* --- HERO SECTION (Responsive) --- */}
        <section className="relative pt-8 md:pt-24 pb-12 md:pb-40 px-4 md:px-8 lg:px-12 overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 md:w-[600px] md:h-[600px] rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 md:w-[500px] md:h-[500px] rounded-full bg-emerald-500/5 blur-3xl" />
          </div>

          <div className="max-w-[1400px] mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* Left Content */}
              <div className="text-center lg:text-left space-y-6 md:space-y-8 px-4">
                {/* Badge */}
                <div className="flex justify-center lg:justify-start">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 rounded-full border backdrop-blur-sm ${isDarkMode ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-emerald-500/20 text-emerald-600 bg-emerald-500/10'}`}>
                    <Sparkles size={12} className="md:hidden" />
                    <Sparkles size={14} className="hidden md:block" />
                    <span className="text-[9px] md:text-[11px] font-black uppercase tracking-[0.3em]">SYSTEM_ONLINE_2026</span>
                  </div>
                </div>

                {/* Main Heading */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black italic uppercase leading-[0.9] tracking-tighter">
  <span className="block text-zinc-500/50">WE</span>
  <span className={`bg-gradient-to-r ${isDarkMode ? 'from-emerald-400 via-emerald-500 to-emerald-600' : 'from-emerald-600 via-emerald-500 to-emerald-400'} bg-clip-text text-transparent`}>
    THRIFT.
  </span>
</h1>

                {/* Description */}
                <p className="text-sm md:text-lg max-w-xs md:max-w-md mx-auto lg:mx-0 leading-relaxed font-bold uppercase tracking-wider opacity-80">
                  Curated global archive. Premium streetwear units. Verified authenticity.
                  Sustainable luxury redefined.
                </p>

                {/* CTA Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
  <Link 
    to="/products"
    className={`w-full sm:w-auto text-center px-8 py-4 md:px-12 md:py-5 rounded-2xl font-black uppercase tracking-[0.3em] text-[11px] md:text-[12px] transition-all duration-500 hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl ${isDarkMode ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/90'}`}
  >
    ACCESS_ARCHIVE
  </Link>
  
  {/* Naya Bulk Order Button */}
  <button 
    onClick={() => setIsBulkModalOpen(true)}
    className={`w-full sm:w-auto text-center px-8 py-4 md:px-12 md:py-5 rounded-2xl font-black uppercase tracking-[0.3em] text-[11px] md:text-[12px] transition-all duration-500 border ${isDarkMode ? 'border-white/20 hover:border-emerald-500/50 hover:text-emerald-400' : 'border-black/20 hover:border-emerald-500/30 hover:text-emerald-600'}`}
  >
    BULK_INQUIRY
  </button>
</div>

                {/* Stats */}
                <div className="pt-8 grid grid-cols-3 gap-4 max-w-sm mx-auto lg:mx-0">
                  {[
                    { value: "5K+", label: "UNITS" },
                    { value: "99%", label: "AUTHENTIC" },
                    { value: "24H", label: "SHIPPING" },
                  ].map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-2xl md:text-3xl font-black italic">{stat.value}</div>
                      <div className="text-[8px] md:text-[9px] font-black uppercase tracking-widest opacity-60">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Image */}
              <div className="relative mt-8 lg:mt-0">
                <div className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] border-2 border-white/10 shadow-3xl">
                  <img
                    src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=800"
                    alt="Archive"
                    className="w-full aspect-[3/4] md:aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-1000 hover:scale-105"
                    crossOrigin="anonymous"
                  />
                  {/* Floating Card */}
                  <div className="absolute bottom-4 left-4 right-4 md:bottom-8 md:left-8 md:right-8 p-4 md:p-6 backdrop-blur-xl bg-black/60 rounded-[1.5rem] border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg md:text-2xl font-black uppercase italic tracking-tighter text-white">GEN_01_VAULT</h3>
                        <p className="text-[9px] md:text-[11px] font-black uppercase tracking-widest text-emerald-400 mt-1">LIMITED_RELEASE</p>
                      </div>
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-center">
                        <Crown size={16} className="text-black" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

<section className="max-w-[1440px] mx-auto px-4 md:px-12 py-12">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-8 px-2">
        <div className="w-2 h-8 bg-emerald-500 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]" />
        <h2 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter">
          OUR CATEGORY
        </h2>
      </div>

      {/* Grid Logic: 
          - Desktop: grid-cols-4 (4 per line)
          - Mobile/Tab: flex overflow-x-auto (Scrollable)
      */}
      <div className="flex md:grid md:grid-cols-4 overflow-x-auto no-scrollbar gap-4 md:gap-6 pb-6 snap-x snap-mandatory">
        {categories.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.slug}`}
            className={`
              flex-shrink-0 
              snap-start
              /* Mobile: Calculate width to show exactly 4 items in view minus gaps */
              w-[calc(25%-12px)] md:w-full 
              min-w-[85px] md:min-w-0
              aspect-square 
              flex flex-col items-center justify-center 
              rounded-2xl md:rounded-[3rem] 
              border transition-all duration-500
              hover:-translate-y-2 group
              ${isDarkMode 
                ? "bg-[#0a0a0a] border-white/5 hover:border-emerald-500/40 shadow-2xl" 
                : "bg-white border-black/5 shadow-lg hover:border-black/20"}
            `}
          >
            {/* Icon Container */}
            <div className={`
              mb-3 p-3 md:p-5 rounded-2xl md:rounded-3xl transition-all duration-500
              group-hover:scale-110
              ${isDarkMode 
                ? "bg-white/5 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-black" 
                : "bg-black/5 text-black group-hover:bg-black group-hover:text-white"}
            `}>
              {cat.icon}
            </div>

            {/* Category Name */}
            <span className={`
              text-[7px] md:text-[11px] font-black uppercase tracking-[0.2em] text-center px-2 leading-tight
              ${isDarkMode ? "text-zinc-400 group-hover:text-white" : "text-zinc-600 group-hover:text-black"}
            `}>
              {cat.name.replace("_", " ")}
            </span>
          </Link>
        ))}
      </div>
    </section>

        {/* --- CURRENT DROPS SECTION (Responsive Slider) --- */}
        {/* --- CURRENT DROPS SECTION --- */}
<section className="max-w-[1440px] mx-auto py-16 md:py-24 px-4 md:px-12">
  <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 border-b border-white/5 pb-8">
    <div className="space-y-2">
      <h2 className="text-4xl md:text-6xl font-black italic uppercase tracking-tighter">
        Current_Drops
      </h2>
      <p className="text-[10px] md:text-[12px] font-black text-emerald-500 uppercase tracking-[0.4em] italic">
        Newly_Secured_Assets
      </p>
    </div>
    <Link to="/products" className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-emerald-500 mt-4 md:mt-0 transition-all hover:gap-4">
      VIEW_ARCHIVE <ArrowRight size={14} />
    </Link>
  </div>

  {loading ? (
    <div className="h-64 flex flex-col items-center justify-center gap-4">
      <RefreshCcw className="animate-spin text-emerald-500" size={32} />
      <p className="text-[10px] font-black uppercase tracking-widest">SCANNING_VAULT...</p>
    </div>
  ) : (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 lg:gap-10">
      {products.map((product) => {
        const availableSize = product.sizeStock?.find(s => s.quantity > 0)?.size || "M";
        // Check if item is already in wishlist
        const isWishlisted = wishlistItems?.some(item => item._id === product._id);
        
        return (
          <div 
            key={product._id} 
            className="group relative cursor-pointer"
            onClick={() => navigate(`/product/${product._id}`)}
          >
            {/* PRODUCT CARD CONTAINER */}
            <div className={`relative aspect-[3/4] overflow-hidden rounded-[1.8rem] md:rounded-[2.5rem] border transition-all duration-700 ${isDarkMode ? 'bg-[#0c0c0c] border-white/5 group-hover:border-emerald-500/30' : 'bg-white border-black/5 group-hover:border-black/20'} shadow-2xl`}>
              
              <img 
                src={product.img || product.image} 
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[0.2] group-hover:grayscale-0"
                crossOrigin="anonymous"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* WISHLIST BUTTON (Fixed Logic) */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleWishlist(product); // Actual Function Call
                  setAlertMsg(isWishlisted ? "REMOVED_FROM_VAULT" : "ASSET_SECURED_IN_WISHLIST");
                }}
                className={`absolute top-4 right-4 p-3 rounded-full backdrop-blur-md border transition-all duration-300 opacity-0 group-hover:opacity-100 translate-y-[-10px] group-hover:translate-y-0 
                  ${isWishlisted 
                    ? 'bg-emerald-500 text-black border-emerald-500' 
                    : 'bg-white/10 text-white border-white/10 hover:bg-white hover:text-black'}`}
              >
                <Sparkles size={16} fill={isWishlisted ? "black" : "none"} /> 
              </button>

              {/* QUICK ADD BUTTON */}
              <div className="absolute inset-x-0 bottom-6 flex justify-center translate-y-10 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 px-4">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuickAdd(product, availableSize);
                  }}
                  className="w-full py-3 bg-emerald-500 text-black rounded-xl md:rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-[0_10px_20px_rgba(16,185,129,0.3)] hover:bg-white transition-colors"
                >
                  QUICK_DEPOSIT
                </button>
              </div>

              {/* SIZE BADGE */}
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-black/60 backdrop-blur-md text-white text-[9px] font-black rounded-full border border-white/10">
                  {availableSize}
                </span>
              </div>
            </div>

            {/* PRODUCT INFO */}
            <div className="mt-6 px-2 space-y-1">
              <div className="flex justify-between items-center">
                <h3 className="text-[11px] md:text-[13px] font-black uppercase italic tracking-tighter opacity-70 group-hover:opacity-100 transition-opacity truncate max-w-[70%]">
                  {product.name}
                </h3>
                <p className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">VERIFIED</p>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-xl md:text-2xl font-black italic">₹{product.price}</p>
                <p className="text-[10px] line-through opacity-30 font-bold">₹{product.price + 500}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  )}
</section>

        {/* --- STORY SECTION (Responsive) --- */}
        <section className={`py-16 md:py-32 px-4 md:px-8 lg:px-12 ${isDarkMode ? 'bg-gradient-to-b from-transparent to-black/20' : 'bg-gradient-to-b from-transparent to-zinc-50/50'}`}>
          <div className="max-w-[1400px] mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
              {/* Left Content */}
              <div className="space-y-6 md:space-y-8">
                <div>
                  <h2 className="text-4xl md:text-6xl lg:text-7xl font-black italic uppercase tracking-tighter leading-none">
                    THE_<span className="text-emerald-500">STORY.</span>
                  </h2>
                  <div className="h-1 w-16 bg-emerald-500 rounded-full mt-4" />
                </div>
                
                <div className="space-y-6">
                  <Quote size={30} className="text-emerald-500/30" />
                  <p className="text-sm md:text-lg font-bold uppercase italic leading-relaxed opacity-80 tracking-wider">
                    Founded in the streets, preserved in the vault. WE THRIFT is a movement towards 
                    sustainable luxury and archival preservation. We believe in the circular economy 
                    and the stories behind every piece.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-4">
                    {['Sustainable', 'Authentic', 'Exclusive', 'Global'].map((tag, i) => (
                      <span key={i} className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                <Link 
                  to="/about-us" 
                  className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-emerald-500 hover:text-emerald-400 transition-colors group"
                >
                  EXPLORE_OUR_JOURNEY
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Right Images */}
              <div className="relative">
                <div className="grid grid-cols-2 gap-4 md:gap-8">
                  <div className="space-y-4 md:space-y-8">
                    <img 
                      src="https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400" 
                      className="rounded-[2rem] aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-700 hover:scale-105"
                      alt="Story 1"
                      crossOrigin="anonymous"
                    />
                    <div className={`h-24 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                      <div className="text-center">
                        <div className="text-3xl font-black italic">2019</div>
                        <div className="text-[9px] font-black uppercase tracking-widest opacity-60">FOUNDED</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 md:space-y-8 pt-8 md:pt-16">
                    <div className={`h-24 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                      <div className="text-center">
                        <div className="text-3xl font-black italic">50+</div>
                        <div className="text-[9px] font-black uppercase tracking-widest opacity-60">COUNTRIES</div>
                      </div>
                    </div>
                    <img 
                      src="https://images.unsplash.com/photo-1534030347209-467a5b0ad3e6?w-400" 
                      className="rounded-[2rem] aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-700 hover:scale-105 border-2 border-emerald-500/20"
                      alt="Story 2"
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- TRUST BADGES (Responsive Grid) --- */}
    <section className="max-w-[1440px] mx-auto py-16 md:py-24 px-4 md:px-8 lg:px-12">
  {/* Section Header */}
  <div className="text-center mb-10 md:mb-16">
    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[9px] font-black tracking-[0.3em] mb-4 ${isDarkMode ? "bg-emerald-500/5 text-emerald-500 border-emerald-500/20" : "bg-zinc-100 text-zinc-500 border-black/5"}`}>
       SECURITY_PROTOCOL_v2.0
    </div>
    <h3 className="text-3xl md:text-5xl font-black italic uppercase tracking-tighter mb-4 leading-none">
      WHY_<span className="text-emerald-500">TRUST</span>_THE_VAULT
    </h3>
    <div className="h-1 w-20 bg-emerald-500 mx-auto rounded-full" />
  </div>

  {/* Scrollable Container for Mobile / Grid for Desktop */}
  <div className="flex md:grid md:grid-cols-4 gap-4 md:gap-6 lg:gap-8 overflow-x-auto no-scrollbar pb-6 md:pb-0 px-2 md:px-0 snap-x snap-mandatory">
    {[
      { 
        icon: <ShieldCheck size={32} strokeWidth={2.5} />, 
        title: "AUTHENTIC", 
        desc: "VERIFIED_UNITS_ONLY",
        id: "01"
      },
      { 
        icon: <Zap size={32} strokeWidth={2.5} />, 
        title: "EXPRESS", 
        desc: "GLOBAL_VAULT_SHIPPING",
        id: "02"
      },
      { 
        icon: <Recycle size={32} strokeWidth={2.5} />, 
        title: "SUSTAIN", 
        desc: "CIRCULAR_ARCHIVE_CYCLE",
        id: "03"
      },
      { 
        icon: <Crown size={32} strokeWidth={2.5} />, 
        title: "PREMIUM", 
        desc: "VAULTED_LEGACY_PIECES",
        id: "04"
      }
    ].map((item, i) => (
      <div 
        key={i} 
        className={`flex-shrink-0 w-[280px] md:w-auto snap-center p-8 rounded-[2.5rem] border transition-all duration-700 relative overflow-hidden group ${
          isDarkMode 
          ? 'border-white/5 bg-gradient-to-br from-[#0a0a0a] to-[#050505] hover:border-emerald-500/30' 
          : 'border-black/5 bg-white shadow-xl hover:shadow-emerald-500/10 hover:border-emerald-500/20'
        }`}
      >
        {/* Background Accent Ornament */}
        <div className="absolute -top-4 -right-4 text-emerald-500/5 font-black text-7xl italic group-hover:scale-110 transition-transform duration-700">
          {item.id}
        </div>

        {/* Icon with Brand Glow */}
        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-3xl mb-8 flex items-center justify-center transition-all duration-500 
          ${isDarkMode 
            ? 'bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-black' 
            : 'bg-black text-white group-hover:bg-emerald-500'
          } shadow-2xl group-hover:shadow-emerald-500/40`}
        >
          {item.icon}
        </div>

        {/* Text Content */}
        <div className="relative z-10">
          <h4 className="font-black uppercase italic tracking-[0.2em] text-[13px] md:text-[15px] mb-3 group-hover:text-emerald-500 transition-colors">
            {item.title}
          </h4>
          <p className={`text-[9px] md:text-[10px] font-black uppercase tracking-[0.15em] leading-relaxed opacity-40 group-hover:opacity-80`}>
            {item.desc}
          </p>
        </div>

        {/* Hover Border Glow (Desktop Only) */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-emerald-500/20 rounded-[2.5rem] pointer-events-none transition-all duration-700" />
      </div>
    ))}
  </div>

  {/* Mobile Hint (Visible only on small screens) */}
  <div className="flex justify-center gap-1.5 mt-6 md:hidden">
      {[1, 2, 3, 4].map((_, i) => (
        <div key={i} className="h-1 w-4 bg-emerald-500/20 rounded-full" />
      ))}
  </div>
</section>

        {/* --- NEWSLETTER SECTION (Responsive) --- */}
        <section className="py-16 md:py-32 px-4 md:px-8 lg:px-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="relative inline-block mb-8">
              <Crown className="text-emerald-500 mb-4 mx-auto" size={48} />
              <div className="absolute -inset-4 bg-emerald-500/10 blur-xl rounded-full" />
            </div>
            
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black italic uppercase mb-6 tracking-tighter leading-none">
              INNER_<span className="text-emerald-500">VAULT.</span>
            </h2>
            
            <p className="text-[11px] md:text-[12px] font-black uppercase tracking-widest opacity-60 mb-8 md:mb-12 max-w-md mx-auto">
              GET_EXCLUSIVE_ACCESS_TO_DROPS_AND_EVENTS
            </p>

            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="ENTER_EMAIL_ID" 
                className={`flex-1 px-6 py-4 md:py-5 rounded-2xl font-black text-[11px] md:text-[12px] uppercase outline-none border transition-all focus:border-emerald-500 ${isDarkMode ? 'bg-white/5 border-white/10 text-white focus:bg-white/10' : 'bg-black/5 border-black/10 text-black focus:bg-black/10'}`}
              />
              <button className={`px-8 py-4 md:py-5 rounded-2xl font-black uppercase text-[11px] md:text-[12px] bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-black shadow-lg hover:shadow-xl hover:shadow-emerald-500/20 transition-all duration-300 active:scale-95`}>
                JOIN_VAULT
              </button>
            </div>
          </div>
        </section>
      </main>
      <AnimatePresence>
  {isBulkModalOpen && (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className={`relative w-full max-w-xl rounded-[3rem] p-8 md:p-12 border ${isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200 text-black'}`}>
        
        <button onClick={() => setIsBulkModalOpen(false)} className="absolute top-8 right-8 text-zinc-500 hover:text-emerald-500 transition-colors">
          <X size={24} />
        </button>

        {isSuccess ? (
          <div className="py-12 text-center">
            <div className="w-20 h-20 bg-emerald-500 text-black rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} />
            </div>
            <h3 className="text-3xl font-black uppercase italic mb-2 tracking-tighter">Query_Received</h3>
            <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest">Wait for our protocol email.</p>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <h3 className="text-4xl font-black uppercase italic mb-1 tracking-tighter">Bulk_<span className="text-emerald-500">Inquiry</span></h3>
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em]">Confidential Wholesale Protocol</p>
            </div>

            <form ref={bulkForm} onSubmit={handleBulkSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input name="user_name" required placeholder="NAME / BUSINESS" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                <input name="user_email" type="email" required placeholder="EMAIL ADDRESS" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input name="quantity" type="number" min="100" required placeholder="QTY (MIN 100)" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                <div className="relative">
                  <select name="category" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border appearance-none transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`}>
                    <option>Mixed Vintage</option>
                    <option>Streetwear / Tees</option>
                    <option>Denim / Archive</option>
                    <option>Winter / Jackets</option>
                  </select>
                </div>
              </div>

              <textarea name="message" rows="3" placeholder="YOUR REQUIREMENTS..." className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border resize-none transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />

              <div className="flex gap-4">
                <button disabled={isSending} type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50">
                  {isSending ? <Loader2 className="animate-spin" size={18}/> : "Dispatch Inquiry"}
                </button>
                <button type="button" onClick={openWhatsApp} className="p-5 rounded-2xl border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500 hover:text-black transition-all">
                  <MessageCircle size={20} />
                </button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

    </div>

    </>
  );
}